import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { captureLocation, formatCoordinates } from '../utils/locationTracker';
import './ClockInOut.css';

function ClockInOut({ user, onBack }) {
  const [clockStatus, setClockStatus] = useState(null); // 'in' or 'out'
  const [lastClockEvent, setLastClockEvent] = useState(null);
  const [currentSessionTime, setCurrentSessionTime] = useState(0);
  const [todayTotalHours, setTodayTotalHours] = useState(0);
  const [clockHistory, setClockHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Use a ref for the timer so it always sees latest values
  const clockStatusRef = useRef(null);
  const lastClockEventRef = useRef(null);

  // 1️⃣ Load data once on mount
  useEffect(() => {
    loadClockData();
  }, []);

  // 2️⃣ Separate timer effect — only runs when clockStatus/lastClockEvent actually settle
  useEffect(() => {
    clockStatusRef.current = clockStatus;
    lastClockEventRef.current = lastClockEvent;

    // Clear any running timer first
    // (handled by cleanup below)
  }, [clockStatus, lastClockEvent]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (clockStatusRef.current === 'in' && lastClockEventRef.current) {
        const elapsed = Date.now() - lastClockEventRef.current.timestamp;
        setCurrentSessionTime(elapsed);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []); // ✅ Only set up once — refs handle the latest values

  const loadClockData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const clockRef = collection(db, 'clockEvents');
      const q = query(
        clockRef,
        where('userId', '==', user.id),
        where('timestamp', '>=', today.getTime()),
        where('timestamp', '<', tomorrow.getTime()),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setClockHistory(events);

      if (events.length > 0) {
        const lastEvent = events[0]; // Most recent
        setLastClockEvent(lastEvent);
        lastClockEventRef.current = lastEvent;
        setClockStatus(lastEvent.type);
        clockStatusRef.current = lastEvent.type;
        calculateTodayHours(events);
      } else {
        // No events today — default to clocked OUT
        setClockStatus('out');
        clockStatusRef.current = 'out';
        setLastClockEvent(null);
        lastClockEventRef.current = null;
      }

    } catch (error) {
      console.error('❌ Error loading clock data:', error);
      setClockStatus('out');
      clockStatusRef.current = 'out';
    } finally {
      setLoading(false);
    }
  };

  const calculateTodayHours = (events) => {
    let totalSeconds = 0;
    let clockInTime = null;

    const sortedEvents = [...events].sort((a, b) => a.timestamp - b.timestamp);

    sortedEvents.forEach(event => {
      if (event.type === 'in') {
        clockInTime = event.timestamp;
      } else if (event.type === 'out' && clockInTime) {
        totalSeconds += (event.timestamp - clockInTime) / 1000;
        clockInTime = null;
      }
    });

    // If still clocked in, count time until now
    if (clockInTime) {
      totalSeconds += (Date.now() - clockInTime) / 1000;
    }

    setTodayTotalHours(totalSeconds / 3600);
  };

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      const location = await captureLocation(user, 'ClockIn', { action: 'clock_in' });

      if (!location) {
        alert('❌ Could not capture GPS location. Please enable location services and try again.');
        setActionLoading(false);
        return;
      }

      const clockEvent = {
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        type: 'in',
        timestamp: Date.now(),
        serverTimestamp: serverTimestamp(),
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        device: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        }
      };

      const docRef = await addDoc(collection(db, 'clockEvents'), clockEvent);
      const savedEvent = { id: docRef.id, ...clockEvent };

      // Update state and refs together
      setClockStatus('in');
      clockStatusRef.current = 'in';
      setLastClockEvent(savedEvent);
      lastClockEventRef.current = savedEvent;
      setCurrentSessionTime(0);

      // Reload full history
      await loadClockData();
      showToast('✅ Clocked In successfully!');

    } catch (error) {
      console.error('❌ Clock In error:', error);
      alert('❌ Failed to clock in. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      const location = await captureLocation(user, 'ClockOut', { action: 'clock_out' });

      if (!location) {
        alert('❌ Could not capture GPS location. Please enable location services and try again.');
        setActionLoading(false);
        return;
      }

      const sessionDuration = lastClockEventRef.current
        ? (Date.now() - lastClockEventRef.current.timestamp) / 1000
        : 0;

      const clockEvent = {
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        type: 'out',
        timestamp: Date.now(),
        serverTimestamp: serverTimestamp(),
        sessionDuration: sessionDuration,
        sessionDurationFormatted: formatDuration(sessionDuration),
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        device: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        }
      };

      const docRef = await addDoc(collection(db, 'clockEvents'), clockEvent);
      const savedEvent = { id: docRef.id, ...clockEvent };

      // Update state and refs together
      setClockStatus('out');
      clockStatusRef.current = 'out';
      setLastClockEvent(savedEvent);
      lastClockEventRef.current = savedEvent;
      setCurrentSessionTime(0);

      // Reload full history
      await loadClockData();
      showToast(`✅ Clocked Out! Session: ${formatDuration(sessionDuration)}`);

    } catch (error) {
      console.error('❌ Clock Out error:', error);
      alert('❌ Failed to clock out. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString('en-KE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const showToast = (message) => {
    const existingToasts = document.querySelectorAll('.toast-notification');
    existingToasts.forEach(toast => toast.remove());

    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.innerHTML = `
      <div class="toast-icon">⏰</div>
      <div class="toast-message">${message}</div>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.remove(), 3000);
  };

  // ─── LOADING STATE ───────────────────────────────────────
  if (loading) {
    return (
      <div className="clock-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading clock data...</p>
        </div>
      </div>
    );
  }

  // ─── MAIN RENDER ─────────────────────────────────────────
  return (
    <div className="clock-page">
      {/* Header */}
      <div className="clock-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>⏰ Clock In/Out</h1>
      </div>

      {/* Current Status Card */}
      <div className="status-card">
        <div className="status-content">
          <div className={`status-indicator-large ${clockStatus === 'in' ? 'clocked-in' : 'clocked-out'}`}>
            {clockStatus === 'in' ? '✓' : '○'}
          </div>
          <div className="status-info">
            <h2>{clockStatus === 'in' ? 'CLOCKED IN' : 'CLOCKED OUT'}</h2>
            {clockStatus === 'in' && lastClockEvent && (
              <p>Since {formatTime(lastClockEvent.timestamp)}</p>
            )}
            {clockStatus === 'out' && lastClockEvent && (
              <p>Last clock out: {formatTime(lastClockEvent.timestamp)}</p>
            )}
            {clockStatus === 'out' && !lastClockEvent && (
              <p>Not clocked in today</p>
            )}
          </div>
        </div>

        {/* Live Session Timer — only when clocked in */}
        {clockStatus === 'in' && (
          <div className="session-timer">
            <div className="timer-label">Current Session</div>
            <div className="timer-value">{formatDuration(currentSessionTime / 1000)}</div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={clockStatus === 'in' ? handleClockOut : handleClockIn}
          disabled={actionLoading}
          className={`clock-action-btn ${clockStatus === 'in' ? 'clock-out-btn' : 'clock-in-btn'}`}
        >
          {actionLoading ? (
            <><span className="spinner-small"></span> Processing...</>
          ) : clockStatus === 'in' ? (
            <>🔴 Clock Out</>
          ) : (
            <>🟢 Clock In</>
          )}
        </button>
      </div>

      {/* Today's Summary */}
      <div className="today-summary">
        <h3>Today's Summary</h3>
        <div className="summary-stats">
          <div className="summary-item">
            <div className="summary-icon">⏱️</div>
            <div className="summary-data">
              <div className="summary-value">{todayTotalHours.toFixed(2)} hrs</div>
              <div className="summary-label">Total Hours</div>
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-icon">📊</div>
            <div className="summary-data">
              <div className="summary-value">{clockHistory.filter(e => e.type === 'out').length}</div>
              <div className="summary-label">Sessions</div>
            </div>
          </div>
          <div className="summary-item">
            <div className="summary-icon">🕐</div>
            <div className="summary-data">
              <div className="summary-value">{clockHistory.length}</div>
              <div className="summary-label">Clock Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Clock History */}
      <div className="clock-history-section">
        <h3>Today's Activity</h3>
        
        {clockHistory.length === 0 ? (
          <div className="empty-state-clock">
            <p>No clock events yet today</p>
          </div>
        ) : (
          <div className="clock-history-list">
            {clockHistory.map((event) => (
              <div key={event.id} className="clock-event-card">
                <div className="event-icon-wrapper">
                  <div className={`event-icon ${event.type === 'in' ? 'event-in' : 'event-out'}`}>
                    {event.type === 'in' ? '→' : '←'}
                  </div>
                </div>
                
                <div className="event-content">
                  <div className="event-header">
                    <span className={`event-type ${event.type === 'in' ? 'type-in' : 'type-out'}`}>
                      {event.type === 'in' ? 'Clock In' : 'Clock Out'}
                    </span>
                    <span className="event-time">{formatTime(event.timestamp)}</span>
                  </div>
                  
                  {event.location && (
                    <div className="event-location">
                      📍 {formatCoordinates(event.location.latitude, event.location.longitude, event.location.accuracy)}
                    </div>
                  )}
                  
                  {event.sessionDurationFormatted && (
                    <div className="event-duration">
                      Duration: {event.sessionDurationFormatted}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClockInOut;
